import React, { useMemo, useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { getSecureImageUrl, handleImageError } from '../../utils/imageUtils';

/**
 * Generic search results component for adding items to collections/wishlists
 * @param {Object} props - Component props
 * @param {Array} props.results - Array of search results
 * @param {boolean} props.loading - Whether search is currently loading
 * @param {string} props.error - Error message if search failed
 * @param {function} props.onAddItem - Function to handle adding an item
 * @param {function} props.onLoadMore - Function to handle loading more results
 * @param {boolean} props.hasMoreResults - Whether there are more results to load
 * @param {string} props.itemType - Type of item ('game', 'console', 'lego')
 * @param {Object} props.renderProps - Custom render props for item-specific fields
 * @returns {JSX.Element} Search results component
 */
const SearchResults = ({
  results,
  loading,
  error,
  onAddItem,
  onLoadMore,
  hasMoreResults,
  itemType = 'item',
  isWishlist = false,
  renderProps = {}
}) => {
  const [selectedGamePlatformById, setSelectedGamePlatformById] = useState({});

  const getPreferredGamePlatform = (item) => {
    if (item?.platform) return item.platform;

    const names = Array.isArray(item?.platforms)
      ? item.platforms
          .map(p => p?.platform?.name || p?.name)
          .filter(Boolean)
      : [];

    if (names.length === 0) return 'Unknown';

    const title = String(item?.name || '').toLowerCase();
    const isVrTitle = title.includes('psvr') || title.includes('vr');

    if (!isVrTitle) {
      if (names.includes('PlayStation 5')) return 'PlayStation 5';
      if (names.includes('PlayStation 4')) return 'PlayStation 4';

      const nonVr = names.filter(n => !/\bvr\b/i.test(n) && !/vr\d/i.test(n));
      if (nonVr.length > 0) return nonVr[0];
    }

    return names[0];
  };

  const getGamePlatformOptions = useMemo(() => {
    if (itemType !== 'game') return {};

    const map = {};
    for (const item of results || []) {
      const id = item?.id;
      if (!id) continue;
      const names = Array.isArray(item?.platforms)
        ? item.platforms
            .map(p => p?.platform?.name || p?.name)
            .filter(Boolean)
        : [];

      map[id] = Array.from(new Set(names));
    }
    return map;
  }, [itemType, results]);

  const getSelectedGamePlatform = (item) => {
    const id = item?.id;
    const selected = id ? selectedGamePlatformById[id] : null;
    if (selected) return selected;
    return getPreferredGamePlatform(item);
  };

  // Default render functions
  const defaultRenderProps = {
    getImageUrl: (item) => {
      if (itemType === 'game') {
        // For games, try multiple image sources
        return item.background_image || item.cover?.url || item.imageUrl || '';
      }
      if (itemType === 'console') {
        // For consoles, try multiple image sources
        return item.imageUrl || item.image || '';
      }
      if (itemType === 'lego') {
        // For Lego, try multiple image sources
        return item.set_img_url || item.imageUrl || '';
      }
      if (itemType === 'movie') {
        // For movies, try multiple image sources
        return item.Poster !== 'N/A' ? item.Poster : (item.imageUrl || '');
      }
      if (itemType === 'music') {
        // For music, try multiple image sources
        return item.imageUrl || '';
      }
      if (itemType === 'board-game') {
        // For board games, use imageUrl from BGG
        return item.imageUrl || '';
      }
      if (itemType === 'book') {
        // For books, use imageUrl
        return item.imageUrl || '';
      }
      return item.imageUrl || '';
    },
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => {
      if (itemType === 'game') {
        const opts = getGamePlatformOptions[item?.id] || [];
        if (opts.length > 1) return '';

        const platform = getSelectedGamePlatform(item);
        return `Platform: ${platform}`;
      }
      if (itemType === 'console') return `Generation: ${item.generation || 'N/A'}`;
      if (itemType === 'lego') return `Set #: ${item.set_num || 'N/A'}`;
      if (itemType === 'movie') return `Year: ${item.Year || item.year || 'N/A'}`;
      if (itemType === 'music') return `Artist: ${item.artist || 'Unknown'}`;
      if (itemType === 'book') return `Author: ${item.author || 'Unknown'}`;
      if (itemType === 'trading-card') return `${item.game || ''} ${item.setName ? `· ${item.setName}` : ''}`.trim();
      if (itemType === 'manga') return `${item.author || ''}${item.demographic ? ` · ${item.demographic}` : ''}`;
      if (itemType === 'comic') return `Issue #${item.issueNumber || '?'}${item.year ? ` · ${item.year}` : ''}`;
      return '';
    },
    getItemDetails: (item) => {
      if (itemType === 'game') {
        return item.summary ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {item.summary.length > 150 ? `${item.summary.substring(0, 150)}...` : item.summary}
          </Typography>
        ) : null;
      }
      if (itemType === 'lego') {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Year: {item.year || 'N/A'} • Theme: {item.theme_id || 'N/A'}
          </Typography>
        );
      }
      if (itemType === 'movie') {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Director: {item.Director || item.director || 'N/A'} • Genre: {item.Genre || item.genre || 'N/A'}
          </Typography>
        );
      }
      if (itemType === 'music') {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Year: {item.year || 'N/A'} • Genre: {item.genre || 'N/A'} • Format: {item.format || 'Not Specified'}
          </Typography>
        );
      }
      if (itemType === 'book') {
        return (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {item.author || 'Unknown Author'} • {item.year || 'N/A'}{item.genre ? ` • ${item.genre}` : ''}
          </Typography>
        );
      }
      return null;
    },
    formatItemForAdd: (item) => {
      if (itemType === 'game') {
        // Format for game collection/wishlist
        // Extract platform information carefully
        const platform = getSelectedGamePlatform(item);
        
        // Ensure we have a valid rawgId as string
        const rawgId = item.id ? item.id.toString() : '';
        
        // Get image URL with fallbacks
        const imageUrl = item.background_image || item.cover?.url || item.imageUrl || '';
        
        // Get description/summary with fallbacks
        const summary = item.summary || item.description || '';
        
        // Calculate year from release date or use provided year
        const releaseDate = item.released || '';
        const year = item.released ? new Date(item.released).getFullYear() : (item.year || new Date().getFullYear());
        
        const formattedItem = {
          name: item.name,
          platform: platform,
          rawgId: rawgId,
          imageUrl: imageUrl,
          summary: summary,
          releaseDate: releaseDate,
          year: year,
          priority: 'Medium',
          played: false,
          completed: false,
          notes: '',
          price: 0, // Set price to 0 instead of empty string to pass validation
          // Use a valid condition value from the Game model enum
          condition: 'Tested & Working',
          // Use a valid edition value from the Game model enum
          edition: 'Standard Edition'
        };
        
        return formattedItem;
      }
      if (itemType === 'console') {
        // Format for console collection/wishlist
        const name = item.name || '';
        // item.platformId (set by useSearch) is the real IGDB platform ID; item.id is a UI key
        const platformId = ('platformId' in item) ? (item.platformId || 0) : (typeof item.id === 'number' ? item.id : 0);
        const generation = item.generation ? String(item.generation) : '';

        // Fix image URL handling
        let imageUrl = '';
        if (item.platform_logo && item.platform_logo.url) {
          // Ensure HTTPS protocol for image URLs
          imageUrl = item.platform_logo.url.startsWith('//') 
            ? `https:${item.platform_logo.url.replace('t_thumb', 't_cover_big')}`
            : item.platform_logo.url.replace('t_thumb', 't_cover_big');
        } else if (item.image) {
          imageUrl = item.image;
        } else if (item.imageUrl) {
          imageUrl = item.imageUrl;
        } else if (item.logo && item.logo.url) {
          imageUrl = item.logo.url.startsWith('//') 
            ? `https:${item.logo.url}` 
            : item.logo.url;
        }

        // Ensure HTTPS for image URLs
        if (imageUrl && imageUrl.startsWith('http:')) {
          imageUrl = imageUrl.replace('http:', 'https:');
        }

        // Fix manufacturer handling
        let manufacturer = '';
        if (item.platform_family && typeof item.platform_family === 'object' && item.platform_family.name) {
          manufacturer = item.platform_family.name;
        } else if (item.company && typeof item.company === 'object' && item.company.name) {
          manufacturer = item.company.name;
        } else if (typeof item.platform_family === 'string') {
          manufacturer = item.platform_family;
        } else if (item.manufacturer && typeof item.manufacturer === 'string') {
          manufacturer = item.manufacturer;
        } else {
          // Default manufacturers for common platforms
          const manufacturerMap = {
            'PlayStation': 'Sony',
            'Xbox': 'Microsoft',
            'Nintendo': 'Nintendo',
            'Sega': 'Sega',
            'Atari': 'Atari'
          };
          
          for (const [key, value] of Object.entries(manufacturerMap)) {
            if (name.includes(key)) {
              manufacturer = value;
              break;
            }
          }
          
          if (!manufacturer) manufacturer = 'Unknown';
        }

        // Use name as model if not specified
        const model = item.model || name || ''; 

        // Determine year
        let year = null;
        if (item.first_release_date) {
          year = new Date(item.first_release_date * 1000).getFullYear();
        } else if (item.release_dates && Array.isArray(item.release_dates) && item.release_dates.length > 0 && item.release_dates[0].y) {
          year = item.release_dates[0].y;
        } else if (item.year) {
          year = parseInt(item.year);
        } else if (item.released) {
          year = new Date(item.released).getFullYear();
        } else {
          // Default to current year if no year is provided
          year = new Date().getFullYear();
        }

        // Handle versions
        let versionsString = 'Standard';
        if (Array.isArray(item.versions) && item.versions.length > 0) {
          versionsString = item.versions.map(v => v.name).join(', ');
        } else if (typeof item.versions === 'string') {
          versionsString = item.versions;
        } else if (item.version) {
          versionsString = item.version;
        }

        const formattedItem = {
          name: name,
          platformId: platformId,
          generation: generation,
          imageUrl: imageUrl,
          manufacturer: manufacturer,
          model: model,
          year: year,
          alternative_name: item.alternative_name || '',
          description: item.summary || '', // IGDB summary often serves as description
          summary: item.summary || '',
          versions: versionsString,
          condition: 'Excellent (EX)', // Using default value from Console model schema
          price: 0, // Set price to 0 instead of empty string to pass validation
          priority: 'Medium',
          notes: ''
        };
        
        return formattedItem;
      }
      if (itemType === 'lego') {
        // Format for lego collection/wishlist
        // Attempt to get theme name, fallback to theme_id as string
        let themeName = 'Unknown';
        let themeId = '';
        
        if (item.theme_name) {
          themeName = item.theme_name;
        } else if (item.theme && typeof item.theme === 'object' && item.theme.name) {
          themeName = item.theme.name;
        } else if (item.theme_id) {
          themeName = String(item.theme_id); // Fallback to theme_id as string if name not found
        }
        
        // Extract theme ID - this is critical for the wishlist to work properly
        if (item.theme_id) {
          themeId = String(item.theme_id);
        } else if (item.theme && typeof item.theme === 'object' && item.theme.id) {
          themeId = String(item.theme.id);
        } else {
          // If no theme ID is found, use a default value to prevent null/undefined issues
          themeId = '0';
        }

        // Ensure we have a valid set number - this is critical for the wishlist to work properly
        let setNum = '';
        if (item.set_num) {
          setNum = item.set_num;
        } else if (item.setNum) {
          setNum = item.setNum;
        } else if (item.set_number) {
          setNum = item.set_number;
        } else if (item.id) {
          // Use ID as fallback if no set number is available
          setNum = String(item.id);
        } else {
          // Generate a placeholder set number if none exists
          setNum = `TEMP-${Math.floor(Math.random() * 10000)}`;
        }
        
        // Ensure we have a valid image URL
        let imageUrl = '';
        if (item.set_img_url) {
          imageUrl = item.set_img_url;
        } else if (item.image_url) {
          imageUrl = item.image_url;
        } else if (item.imageUrl) {
          imageUrl = item.imageUrl;
        } else if (item.image) {
          imageUrl = item.image;
        }
        
        // Make sure image URL uses HTTPS
        if (imageUrl && imageUrl.startsWith('http:')) {
          imageUrl = imageUrl.replace('http:', 'https:');
        } else if (imageUrl && imageUrl.startsWith('//')) {
          imageUrl = `https:${imageUrl}`;
        }
        
        // Ensure we have a valid year
        let year = null;
        if (item.year) {
          year = parseInt(item.year);
        } else if (item.released) {
          year = new Date(item.released).getFullYear();
        } else {
          // Default to current year if no year is provided
          year = new Date().getFullYear();
        }
        
        // Ensure we have a valid number of parts
        let numParts = 0;
        if (item.num_parts) {
          numParts = parseInt(item.num_parts);
        } else if (item.numParts) {
          numParts = parseInt(item.numParts);
        } else if (item.piece_count) {
          numParts = parseInt(item.piece_count);
        }
        
        const formattedItem = {
          name: item.name || '',
          // Include both field names to satisfy both frontend and backend
          setNum: setNum, // For frontend form field
          setNumber: setNum, // For backend API
          set_img_url: imageUrl, // For frontend form field
          imageUrl: imageUrl, // For backend API
          year: year,
          theme: themeName, // Use extracted/fallback theme name
          themeId: themeId, // For frontend form field
          numParts: numParts,
          priority: 'Medium',
          notes: '',
          price: 0, // Set price to 0 instead of empty string to pass validation
          condition: 'New'
        };
        
        return formattedItem;
      }
      if (itemType === 'movie') {
        // Format for movie collection/wishlist
        // Create a properly formatted movie object with all required fields
        const formattedItem = {
          name: item.Title || item.name || '',
          imdbId: item.imdbID || item.imdbId || item.id || '', // Using lowercase 'd' to match the Movie model
          year: item.Year || item.year || new Date().getFullYear().toString(),
          director: item.Director || item.director || '',
          genre: item.Genre || item.genre || '',
          runtime: item.Runtime || item.runtime || '',
          plot: item.Plot || item.plot || '',
          imageUrl: (item.Poster && item.Poster !== 'N/A') ? item.Poster : (item.imageUrl || ''),
          format: 'DVD', // Using a valid format from the Movie model enum
          condition: 'Good / Acceptable', // Using a valid condition from the Movie model enum
          edition: 'Standard Edition',
          priority: undefined, // No priority for regular collection items
          watched: false,
          price: 0 // Set price to 0 instead of empty string to pass validation
        };
        
        return formattedItem;
      }
      if (itemType === 'music') {
        // Format for music collection/wishlist
        // Normalize tracks to newline-separated string
        let tracksString = '';
        if (Array.isArray(item.tracks)) {
          tracksString = item.tracks
            .map(t => (typeof t === 'string' ? t : (t && t.title) ? `${t.position ? `${t.position}. ` : ''}${t.title}` : ''))
            .filter(Boolean)
            .join('\n');
        } else if (typeof item.tracks === 'string') {
          tracksString = item.tracks;
        }

        // Use enum-safe defaults required by Music schema
        const formattedItem = {
          name: item.name || item.title || '',
          artist: item.artist || '',
          year: item.year || new Date().getFullYear().toString(),
          genre: item.genre || 'Unknown Genre',
          label: item.label || 'Unknown Label',
          tracks: tracksString,
          imageUrl: item.imageUrl || '',
          format: ['Vinyl','CD','Cassette','8-Track','Digital','Box Set','Other'].includes(item.format) ? item.format : 'CD',
          condition: ['Mint (M)','Near Mint (NM)','Very Good Plus (VG+)','Very Good (VG)','Good (G)','Poor (P)','Factory Sealed','New','Like New','Good / Acceptable','For Parts / Not Working'].includes(item.condition) ? item.condition : 'Very Good (VG)',
          edition: ['Standard Release','Limited Edition','First Pressing','Reissue','Remastered','Deluxe Edition','Collector\'s Edition','Anniversary Edition','Promotional Copy','Import','Colored Vinyl','Picture Disc','Box Set','Not Specified'].includes(item.edition) ? item.edition : 'Standard Release',
          priority: item.priority || undefined,
          listened: false,
          price: 0 // numeric is fine; server coerces to string
        };
        
        return formattedItem;
      }

      if (itemType === 'book') {
        return {
          name: item.name || '',
          author: item.author || '',
          year: item.year || '',
          genre: item.genre || '',
          publisher: item.publisher || '',
          isbn13: item.isbn13 || '',
          isbn10: item.isbn10 || '',
          pages: item.pages || '',
          imageUrl: item.imageUrl || '',
          openLibraryKey: item.openLibraryKey || '',
          series: item.series || '',
          seriesNumber: item.seriesNumber || '',
          language: item.language || '',
          format: ['Hardback', 'Paperback', 'eBook', 'Audiobook', 'Leather Bound', 'Board Book', 'Other'].includes(item.format) ? item.format : 'Paperback',
          read: false,
          price: 0
        };
      }

      if (itemType === 'board-game') {
        return {
          name: item.name || '',
          bggId: item.bggId || item.id || null,
          designers: item.designers || '',
          publisher: item.publisher || '',
          year: item.year || null,
          minPlayers: item.minPlayers || null,
          maxPlayers: item.maxPlayers || null,
          playingTime: item.playingTime || null,
          minAge: item.minAge || null,
          category: item.category || '',
          genre: item.genre || item.category || '',
          language: 'English',
          imageUrl: item.imageUrl || '',
          played: false,
          rating: null,
          favourite: false,
          notes: '',
          price: ''
        };
      }

      if (itemType === 'trading-card') {
        // Derive game from whichever ID field is present — search results don't carry a game field
        const derivedGame = item.pokemonTcgId ? 'Pokémon'
          : item.scryfallId ? 'Magic: The Gathering'
          : item.yugiohId ? 'Yu-Gi-Oh!'
          : item.game || 'Other';
        return {
          name: item.name || '',
          game: derivedGame,
          setName: item.setName || '',
          cardNumber: item.cardNumber || '',
          rarity: item.rarity || '',
          artist: item.artist || '',
          year: item.year || null,
          language: item.language || 'English',
          imageUrl: item.imageUrl || '',
          pokemonTcgId: item.pokemonTcgId || null,
          scryfallId: item.scryfallId || null,
          yugiohId: item.yugiohId || null,
          condition: 'Near Mint',
          quantity: 1,
          favourite: false,
          rating: null,
          notes: '',
          price: 0,
        };
      }

      if (itemType === 'manga') {
        return {
          name: item.name || '',
          author: item.author || '',
          genre: item.genre || '',
          demographic: item.demographic || null,
          volume: item.volume || 1,
          totalVolumes: item.totalVolumes || null,
          format: item.format || 'Tankōbon',
          year: item.year || null,
          imageUrl: item.imageUrl || '',
          mangaDexId: item.mangaDexId || null,
          read: false,
          favourite: false,
          rating: null,
          notes: '',
          price: 0,
        };
      }

      if (itemType === 'comic') {
        return {
          name: item.name || '',
          series: item.series || item.name || '',
          issueNumber: item.issueNumber || item.issue_number || '1',
          publisher: item.publisher || '',
          year: item.year || null,
          imageUrl: item.imageUrl || '',
          comicVineId: item.comicVineId || null,
          condition: null,
          isGraded: false,
          favourite: false,
          rating: null,
          notes: '',
          price: 0,
        };
      }

      // Fallback for unknown or unhandled itemType
      console.warn(`[SearchResults] formatItemForAdd: Unhandled itemType "${itemType}". Item data:`, item);
      return {
        name: item.name || 'Unknown Item Name',
        error: `Unhandled itemType: ${itemType}`,
        originalItemData: JSON.stringify(item) // Send stringified original item for easier debugging if it reaches backend
      };
    },
  };

  // Merge default render props with custom render props
  const mergedRenderProps = { ...defaultRenderProps, ...renderProps };

  if (loading && (!results || results.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!results || results.length === 0) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography align="center">
          No results found. Try a different search term.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Search Results
      </Typography>
      <Grid container spacing={3}>
        {results.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={item.id || item.set_num || index}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {mergedRenderProps.getImageUrl(item) ? (
                <Box sx={{ pt: '56.25%', position: 'relative' }}>
                  <img
                    src={getSecureImageUrl(mergedRenderProps.getImageUrl(item))}
                    alt={mergedRenderProps.getItemName(item)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      padding: '10px'
                    }}
                    onError={(e) => {
                      handleImageError(e, mergedRenderProps.getItemName(item), itemType);
                    }}
                    loading="lazy"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    pt: '56.25%',
                    position: 'relative',
                    bgcolor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    No Image
                  </Typography>
                </Box>
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" component="div">
                  {mergedRenderProps.getItemName(item)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {mergedRenderProps.getItemSubtitle(item)}
                </Typography>

                {itemType === 'game' && (getGamePlatformOptions[item?.id] || []).length > 1 && (
                  <Box sx={{ mt: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`platform-select-label-${item.id}`}>Platform</InputLabel>
                      <Select
                        labelId={`platform-select-label-${item.id}`}
                        value={getSelectedGamePlatform(item)}
                        label="Platform"
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedGamePlatformById(prev => ({
                            ...prev,
                            [item.id]: val
                          }));
                        }}
                      >
                        {(getGamePlatformOptions[item?.id] || []).map((p) => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
                {mergedRenderProps.getItemDetails(item)}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => onAddItem(mergedRenderProps.formatItemForAdd(item))}
                >
                  {isWishlist ? 'Add to Wishlist' : `Add to ${itemType === 'game' ? 'Games' : itemType === 'console' ? 'Consoles' : itemType === 'movie' ? 'Movies' : itemType === 'music' ? 'Music' : itemType === 'book' ? 'Books' : itemType === 'board-game' ? 'Board Games' : itemType === 'trading-card' ? 'Cards' : itemType === 'manga' ? 'Manga' : itemType === 'comic' ? 'Comics' : 'LEGO Sets'}`}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {hasMoreResults && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SearchResults;
